import React, { useEffect, useState } from "react";
import "../styles/add-contact-popup.scss";
import CircularProgress from "../actions/CircularProgress";
import { useUpdateState } from "../utilities/utilities";
import { GuiButton } from "../components/global-ui/gui-button";
import { GuiSelect } from "../components/global-ui/gui-select";
import { GuiTextbox } from "../components/global-ui/gui-textbox";
import { GuiRadio } from "../components/global-ui/gui-radio";
import { CustomMenuList } from "../components/global-ui/gui-custom-menu-list";
import { useOnFirstLoad } from "../utilities/hooks";
import { SettingsApi } from "../actions/settings-action";
import { GuiCheckbox } from "../components/global-ui/gui-checkbox";
import { showToast } from "../components/toast";
import { USStates, CustomContactTypes } from "../utilities/constants";
import { useSelector } from "react-redux";
import { contacts } from "../api/contacts-api";
import CustomContactType from "./CustomContactType";
import { GuiCustomSelect } from "../components/global-ui/gui-custom-select";
import { Modal } from "../components/modal";
import { CustomContacsApi } from "../actions/custom-contacts-actions";

const AddContactPopup = ({
  userId,
  contactModal,
  onClose,
  textPreferredLanguage,
  voicePreferredLanguage,
  onSuccessfulAdd,
  editedContact,
  onDeleteClick,
  onEditSuccess,
  onCustomContactEdit,
}) => {
  let isMobile = useSelector((store) => store.page.isMobile);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  let states = USStates;
  let [state, updateState] = useUpdateState({
    loading: true,
    isAddDisabled: false,
    contactType: "1-false",
    typeOptions: [],
    phoneNumber: "",
    ext: "",
    isValid: true,
    voiceOptions: [],
    textOptions: [],
    enableTextMsg: true,
    enableVoiceMsg: true,
    customTypeMethod: CustomContactTypes.HomePhone,
    customTypeName: "",
    id: 0,
  });
  let [personAddress, updatePersonAddress] = useUpdateState({
    address: "",
    city: "",
    state: "",
    zip: "",
    comment: "",
  });
  const [customTypePopup, setCustomTypePopup] = useState(false);
  const [editPopupData, setEditPopupData] = useState(null);
  const [showDeleteContactModal, setShowDeleteContactModal] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    address: false,
    city: false,
    zip: false,
    state: false,
  });
  let [errors, setErrors] = useState({
    phoneNumber: "",
    email: "",
  });
  useEffect(() => {
    if (state.typeOptions.length > 0) {
      if (editedContact.contactTypeMethod === CustomContactTypes.Address) {
        updatePersonAddress({
          address: editedContact?.address,
          city: editedContact?.city,
          state: editedContact?.state,
          zip: editedContact?.zip?.trim(),
          comment: editedContact?.address_comment,
        });
        updateState({
          contactType: state.typeOptions.find(
            (type) => type.label === editedContact.contactTypeName,
          )?.value,
          customTypeMethod: editedContact?.contactTypeMethod,
          customTypeName: editedContact?.contactTypeName,
          isCustomContactType: editedContact?.isCustomType,
          id: editedContact?.id,
        });
      } else if (editedContact && Object.keys(editedContact).length > 0) {
        updateState({
          contactType: state.typeOptions.find(
            (type) => type.label === editedContact.contactTypeName,
          ).value,
          isCustomContactType: state.typeOptions.find(
            (type) => type.label === editedContact.contactTypeName,
          ).isCustomContactType,
          phoneNumber: editedContact.phone || "",
          ext: editedContact.ext || "",
          email:
            editedContact.customTypeMethod === CustomContactTypes.Email
              ? editedContact.phone
              : "",
          isValid: editedContact.invalidFlag === 0,
          enableTextMsg: !editedContact.textMessageNotEnabled,
          enableVoiceMsg: !editedContact.voiceNotEnabled,
          voiceType: editedContact.voiceLanguageId,
          textType: editedContact.textLanguageCode,
          customTypeMethod: editedContact.customTypeMethod,
          id: editedContact?.id,
        });
      }
    }
  }, [state.typeOptions]);
  function getCustomContactTypes() {
    if (userId) {
      contacts.getAllContactTypesForSpecificUser(userId).then((result) => {
        if (result.success) {
          const transformedContacts = result.contactTypes.map(
            ({
              id,
              customTypeName,
              isCustomContactType,
              customTypeMethod,
            }) => ({
              value: `${id}-${isCustomContactType}`,
              id: id,
              label: customTypeName,
              isCustomContactType: isCustomContactType,
              customTypeMethod: customTypeMethod,
            }),
          );
          updateState({ typeOptions: transformedContacts });
        }
      });
    } else {
      contacts.getContactTypes().then((result) => {
        if (result.success) {
          const transformedContacts = result.contactTypes.map(
            ({
              id,
              customTypeName,
              isCustomContactType,
              customTypeMethod,
            }) => ({
              value: `${id}-${isCustomContactType}`,
              id: id,
              label: customTypeName,
              isCustomContactType: isCustomContactType,
              customTypeMethod: customTypeMethod,
            }),
          );
          updateState({ typeOptions: transformedContacts });
        }
      });
    }
  }
  useOnFirstLoad(() => {
    updateState({ loading: false });
    getCustomContactTypes();

    if (voicePreferredLanguage.length > 0 || textPreferredLanguage.length > 0) {
      const voiceLanguageOptions = voicePreferredLanguage.map((obj) => ({
        label: obj.name,
        value: obj.voiceId,
      }));
      const textLanguageOptions = textPreferredLanguage.map((obj) => ({
        label: obj.name,
        value: obj.textCode,
      }));
      updateState({
        textOptions: textLanguageOptions,
        voiceOptions: voiceLanguageOptions,
      });
    }
  });

  const handleChange = (name, option) => {
    if (name === "contactType") {
      updateState({
        [name]: option.value,
        isCustomContactType: option.isCustomContactType,
        customTypeMethod: option.customTypeMethod,
        customTypeName: option.label,
      });
    } else {
      updateState({
        [name]: option,
      });
    }

    if (name === "phoneNumber") {
      setErrors({ phoneNumber: "" });
    }
  };
  const handleKeyDown = (e) => {};
  const handleRadioChange = (name, value) => {
    updateState({ ...state, [name]: value === "true" });
  };

  const validateFields = () => {
    const result =
      state.customTypeMethod === CustomContactTypes.Email
        ? validateEmail(state.email)
        : validateAndFormatPhoneNumber(state.phoneNumber);

    if (result.isValid) {
      state.customTypeMethod !== CustomContactTypes.Email &&
        updateState({ phoneNumber: result.formattedNumber });
      return true;
    } else {
      state.customTypeMethod === CustomContactTypes.Email
        ? setErrors({ email: result.message })
        : setErrors({ phoneNumber: result.message });
      return false;
    }
  };

  const onUpdateAddress = async () => {
    Object.entries(personAddress).forEach(([key, value]) => {
      if (key !== "comment") {
        if (value === "") {
          setFieldErrors((prevErrors) => ({
            ...prevErrors,
            [key]: "Required Field",
          }));
        } else if (key === "zip") {
          const zipCodeRegex = /^\d{5}(-\d{4})?$/;
          if (!zipCodeRegex.test(value)) {
            setFieldErrors((prevErrors) => ({
              ...prevErrors,
              [key]: "Invalid ZIP format",
            }));
          }
        }
      }
    });
    if (!isFormValid()) {
      return;
    }

    var reqData = {
      address: personAddress.address,
      city: personAddress.city,
      state: personAddress.state,
      zip: personAddress.zip,
      address_comment: personAddress.comment,
      contact_type_name: state.customTypeName,
      is_custom_address: state.isCustomContactType,
      id: state.id,
    };
    if (editedContact && Object.keys(editedContact).length > 0) {
      if (userId) {
        const result = await SettingsApi.updateAddressForSpecificUser(
          reqData,
          userId,
        );
        if (result.success) {
          showToast("success", "Contact Edited");
          onEditSuccess();
        }
        onClose();
        return result;
      } else {
        const result = await SettingsApi.updateAddress(reqData);
        if (result.success) {
          showToast("success", "Contact Edited");
          onEditSuccess();
        }
        onClose();
        return result;
      }
    } else {
      if (userId) {
        const result = await SettingsApi.addAddressForSpecificUser(
          reqData,
          userId,
        );
        if (result.success) {
          showToast("success", "Contact Added");
          onSuccessfulAdd();
        }
        onClose();
        return result;
      } else {
        const result = await SettingsApi.addAddress(reqData);
        if (result.success) {
          showToast("success", "Contact Added");
          onSuccessfulAdd();
        }
        onClose();
        return result;
      }
    }
  };
  const isFormValid = () => {
    return Object.entries(personAddress).every(([key, value]) => {
      if (key === "comment") return true;
      if (value === "") return false;
      if (key === "zip") {
        const zipCodeRegex = /^\d{5}(-\d{4})?$/;
        return zipCodeRegex.test(value);
      }
      return true;
    });
  };
  const handleAddressChange = (name, value) => {
    updatePersonAddress({ [name]: value });
    setFieldErrors((prevErrors) => ({
      ...prevErrors,
      [name]: value === "" ? "Required Field" : false,
    }));
    if (name === "zip") {
      const zipCodeRegex = /^\d{5}(-\d{4})?$/;
      if (!zipCodeRegex.test(value)) {
        setFieldErrors((prevErrors) => ({
          ...prevErrors,
          [name]: "Invalid ZIP format",
        }));
      }
    }
  };

  const backToContact = () => {
    setCustomTypePopup(false);
  };

  const onCustomTypeClick = () => {
    setCustomTypePopup(true);
  };

  const onEditCustomType = (data) => {
    setCustomTypePopup(true);
    setEditPopupData(data);
  };

  const onDeleteCustomType = (data) => {
    setShowDeleteContactModal(true);
    setEditPopupData(data);
  };

  const deleteCustomContact = async (id) => {
    if (id > 0) {
      try {
        if (userId) {
          const response =
            await CustomContacsApi.deleteCustomContactTypeForSpecificUser(
              id,
              userId,
            );
          if (response?.success) {
            showToast("success", "Custom contact removed");
            getCustomContactTypes?.();
          }
        } else {
          const response = await CustomContacsApi.deleteCustomContact(id);

          if (response?.success) {
            showToast("success", "Custom contact removed");
            getCustomContactTypes?.();
          }
        }
      } catch (error) {
        console.error("Error deleting custom contact type:", error);
        showToast("error", "An unexpected error occurred.");
      }
    }
    setShowDeleteContactModal(false);
  };

  const deleteClicked = () => {
    const id =
      editedContact?.contactTypeMethod === CustomContactTypes.Address
        ? editedContact?.id
        : editedContact?.contactId;
    onDeleteClick({
      contactTypeName: editedContact.contactTypeName,
      contactTypeMethod: editedContact?.contactTypeMethod,
      id: id,
    });
  };
  const onAddClick = async () => {
    if (!validateFields()) {
      return;
    }

    updateState({ isAddDisabled: true });
    const payload = {
      isCustomContactType: state.isCustomContactType,
      Phone:
        state.customTypeMethod === CustomContactTypes.Email
          ? state.email
          : state.phoneNumber,
      Ext: state.ext,
      ContactType: state.contactType.split("-")[0],
      NoTextMessage:
        state.customTypeMethod === CustomContactTypes.CellPhone
          ? !state.enableTextMsg
          : false,
      TextMessageOnly: !state.enableVoiceMsg,
      // UpdateContactRequest (backend) reads these two instead of NoTextMessage/TextMessageOnly.
      TextMessageNotEnabled:
        state.customTypeMethod === CustomContactTypes.CellPhone
          ? !state.enableTextMsg
          : false,
      VoiceNotEnabled: !state.enableVoiceMsg,
      InvalidFlag: state.isValid === true ? 0 : 1,
      VoiceLanguageId:
        state.enableVoiceMsg === true
          ? state.voiceType === "Select"
            ? null
            : state.voiceType
          : null,
      TextLanguageCode:
        state.enableTextMsg === true
          ? state.textType === "Select"
            ? ""
            : state.textType
          : "",
    };
    if (editedContact && Object.keys(editedContact).length > 0) {
      if (userId) {
        let response = await SettingsApi.updateContactForSpecificUser(
          { ...payload, contactId: editedContact.contactId },
          userId,
        );
        if (response.success) {
          showToast("success", "Contact Edited");
          onEditSuccess();
        }
        onClose();
        return;
      } else {
        let response = await SettingsApi.updateContact({
          ...payload,
          contactId: editedContact.contactId,
        });
        if (response.success) {
          showToast("success", "Contact Edited");
          onEditSuccess();
        }
        onClose();
      }
    } else {
      if (userId) {
        let response = await SettingsApi.addContactForSpecificUser(
          payload,
          userId,
        );
        if (response.success) {
          showToast("success", "Contact Added");
          onSuccessfulAdd();
        }
        onClose();
      } else {
        let response = await SettingsApi.addContact(payload);
        if (response.success) {
          showToast("success", "Contact Added");
          onSuccessfulAdd();
        }
        onClose();
      }
    }
  };
  function validateEmail(input) {
    if (!input) {
      return { isValid: false, message: "Email is required" };
    }
    if (emailRegex.test(input)) {
      return { isValid: true };
    } else {
      return { isValid: false, message: "Invalid Email format" };
    }
  }
  function validateAndFormatPhoneNumber(input) {
    if (!input) {
      return { isValid: false, message: "Phone number is required" };
    }

    // Define regex patterns for accepted formats
    const regexPattern1 = /^\d{3}-\d{3}-\d{4}$/; // Matches 123-456-7890
    const regexPattern2 = /^\(\d{3}\) \d{3}-\d{4}$/; // Matches (123) 456-7890

    if (regexPattern1.test(input) || regexPattern2.test(input)) {
      // If the input matches one of the accepted formats, return it as valid
      return { isValid: true, formattedNumber: input };
    } else {
      // If the input doesn't match, format it to 123-456-7890
      const cleanedInput = input.replace(/\D/g, ""); // Remove all non-numeric characters
      if (cleanedInput.length === 10) {
        const formattedNumber = `${cleanedInput.slice(
          0,
          3,
        )}-${cleanedInput.slice(3, 6)}-${cleanedInput.slice(6, 10)}`;
        return { isValid: true, formattedNumber };
      } else {
        return { isValid: false, message: "Invalid phone number format" };
      }
    }
  }

  return (
    <>
      {customTypePopup && (
        <CustomContactType
          userId={userId}
          isMobile={isMobile}
          back={backToContact}
          getCustomContactTypes={getCustomContactTypes}
          data={editPopupData}
          onCustomContactEdit={onCustomContactEdit}
        />
      )}
      {!customTypePopup && (
        <div className={`Modal ${contactModal ? "Show" : "hide"}`}>
          {state.loading ? (
            <CircularProgress />
          ) : (
            <>
              <div className="add-modal-header">
                {isMobile && (
                  <span onClick={onClose} className="mr-2">
                    <i className="fa fa-chevron-left close-primary-icon cursor-pointer"></i>
                  </span>
                )}
                <h4>
                  {editedContact && Object.keys(editedContact).length > 0
                    ? `Edit  ${editedContact.contactTypeName}`
                    : "Add Contact"}
                </h4>
                {!isMobile && (
                  <span onClick={onClose}>
                    <i className="fa fa-close close-primary-icon cursor-pointer"></i>
                  </span>
                )}
              </div>

              <div className="add-modal-body">
                <GuiSelect
                  label="Contact Type"
                  name="contactType"
                  isSearchable="true"
                  value={state.contactType}
                  onkeyDown={handleKeyDown}
                  onChange={(name, option) => handleChange(name, option)}
                  options={state.typeOptions}
                  components={{
                    MenuList: CustomMenuList,
                    Option: GuiCustomSelect,
                  }}
                  onCustomType={onCustomTypeClick}
                  onEditCustomType={onEditCustomType}
                  onDeleteCustomType={onDeleteCustomType}
                />
                {state.customTypeMethod === CustomContactTypes.HomePhone && (
                  <span className="helper-text">
                    Home phone numbers do not receive text messages
                  </span>
                )}

                {state.customTypeMethod === CustomContactTypes.Address ? (
                  <div className="address-field">
                    <div className="address-field-row">
                      <GuiTextbox
                        label="Address"
                        name="address"
                        value={personAddress.address}
                        onChange={handleAddressChange}
                        maxLength={500}
                        error={
                          fieldErrors.address && (
                            <span>
                              <i className="fas fa-exclamation-circle"></i>{" "}
                              {fieldErrors.address}
                            </span>
                          )
                        }
                      />
                    </div>
                    <div className="address-field-row">
                      <GuiTextbox
                        label="City"
                        name="city"
                        value={personAddress.city}
                        onChange={handleAddressChange}
                        maxLength={100}
                        error={
                          fieldErrors.city && (
                            <span>
                              <i className="fas fa-exclamation-circle"></i>{" "}
                              {fieldErrors.city}
                            </span>
                          )
                        }
                      />
                    </div>
                    <div className="address-field-row">
                      <GuiSelect
                        label="State"
                        name="state"
                        value={personAddress.state}
                        onChange={(name, option) =>
                          handleAddressChange(name, option.value)
                        }
                        options={states}
                        error={
                          fieldErrors.state && (
                            <span>
                              <i className="fas fa-exclamation-circle"></i>{" "}
                              {fieldErrors.state}
                            </span>
                          )
                        }
                      />
                    </div>
                    <div className="address-field-row">
                      <GuiTextbox
                        label="Zip"
                        name="zip"
                        value={personAddress.zip}
                        zipPattern
                        onChange={handleAddressChange}
                        maxLength={10}
                        error={
                          fieldErrors.zip && (
                            <span>
                              <i className="fas fa-exclamation-circle"></i>{" "}
                              {fieldErrors.zip}
                            </span>
                          )
                        }
                      />
                    </div>
                    <div className="address-field-row">
                      <GuiTextbox
                        label="Comment (Optional)"
                        name="comment"
                        value={personAddress.comment}
                        onChange={handleAddressChange}
                        maxLength={500}
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    {state.customTypeMethod === CustomContactTypes.Email ? (
                      <div className="contact-email">
                        {" "}
                        <GuiTextbox
                          label="Email"
                          name="email"
                          autoComplete="new-password"
                          value={state.email}
                          maxLength={100}
                          onChange={(name, option) =>
                            handleChange(name, option)
                          }
                        />{" "}
                      </div>
                    ) : (
                      <div className="phone-num-ext">
                        <GuiTextbox
                          label="Phone Number"
                          autoComplete="new-password"
                          name="phoneNumber"
                          className="phone-number"
                          format="digits"
                          value={state.phoneNumber}
                          onChange={(name, option) =>
                            handleChange(name, option)
                          }
                          maxLength={15}
                        />

                        <GuiTextbox
                          label="Ext."
                          name="ext"
                          autoComplete="new-password"
                          className="phone-ext"
                          value={state.ext}
                          onChange={(name, option) =>
                            handleChange(name, option)
                          }
                          maxLength={5}
                        />
                      </div>
                    )}
                    {state.customTypeMethod === CustomContactTypes.Email ? (
                      <div className="error-class">
                        {errors.email && (
                          <div className="error">{errors.email}</div>
                        )}
                      </div>
                    ) : (
                      <div className="error-class">
                        {errors.phoneNumber && (
                          <div className="error">{errors.phoneNumber}</div>
                        )}
                      </div>
                    )}

                    <div className="preferred-language">
                      {(state.customTypeMethod ===
                        CustomContactTypes.CellPhone ||
                        state.customTypeMethod ===
                          CustomContactTypes.Email) && (
                        <div className="contact-checkboxes">
                          <div className="checkbox-row">
                            <GuiCheckbox
                              label="Enable Text Messages"
                              name="enableTextMsg"
                              value={state.enableTextMsg}
                              onChange={(name, option) =>
                                handleChange(name, option)
                              }
                            />
                          </div>
                          {state.enableTextMsg && (
                            <div className="checkbox-row contactSelect">
                              <GuiSelect
                                label="Preferred Language"
                                name="textType"
                                value={state.textType}
                                onChange={(name, option) =>
                                  handleChange(name, option.value)
                                }
                                options={state.textOptions}
                              />
                            </div>
                          )}
                          <span className="helper-text">
                            The language your incoming text messages will be in
                          </span>
                        </div>
                      )}
                      {state.customTypeMethod !== CustomContactTypes.Email && (
                        <>
                          <div className="checkbox-row">
                            <GuiCheckbox
                              label="Enable Voice Messages"
                              name="enableVoiceMsg"
                              value={state.enableVoiceMsg}
                              onChange={handleChange}
                            />
                          </div>
                          {state.enableVoiceMsg && (
                            <div className="contactSelect">
                              <GuiSelect
                                label="Preferred Language"
                                name="voiceType"
                                value={state.voiceType}
                                onChange={(name, option) =>
                                  handleChange(name, option.value)
                                }
                                options={state.voiceOptions}
                              />
                            </div>
                          )}
                          <span className="helper-text">
                            The language you will hear text-to-speech messages
                            in
                          </span>
                        </>
                      )}
                    </div>
                    {state.customTypeMethod !== CustomContactTypes.Email && (
                      <div className="contact-status">
                        <span className="contact-status-header">
                          Status of Contact Method:
                        </span>
                        <div className="contact-status-body">
                          <GuiRadio
                            label="Valid"
                            name="isValid"
                            id={true}
                            value={state.isValid}
                            onChange={handleRadioChange}
                          />
                          <GuiRadio
                            label="Invalid"
                            name="isValid"
                            id={false}
                            value={state.isValid}
                            onChange={handleRadioChange}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {/* <div
              className={`modal-footer-hr-divider-${
                state.contactType === 3 ? "cell" : "home"
              }`}
            ></div> */}
                <div className="modal-footer-hr-divider"></div>
                <div className="modal-footer-btns">
                  {editedContact && Object.keys(editedContact).length > 0 ? (
                    <GuiButton className="del-btn" onClick={deleteClicked}>
                      <i className="fa fa-trash"></i>
                      <span>Delete</span>
                    </GuiButton>
                  ) : (
                    <GuiButton className="cancel-btn" onClick={onClose}>
                      Cancel
                    </GuiButton>
                  )}

                  {state.customTypeMethod === CustomContactTypes.Address ? (
                    <GuiButton
                      className="add-btn"
                      disabled={state.isAddDisabled}
                      onClick={onUpdateAddress}
                    >
                      {editedContact && Object.keys(editedContact).length > 0
                        ? "Save"
                        : "Add"}
                    </GuiButton>
                  ) : (
                    <GuiButton
                      className="add-btn"
                      disabled={state.isAddDisabled}
                      onClick={onAddClick}
                    >
                      {editedContact && Object.keys(editedContact).length > 0
                        ? "Save"
                        : "Add"}
                    </GuiButton>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Delete */}
      {showDeleteContactModal && (
        <Modal
          className="plain-modal no-close"
          showModal={showDeleteContactModal}
          onClose={() => setShowDeleteContactModal(false)}
        >
          <p>
            Are you sure you want to permanently remove {editPopupData?.label}{" "}
            from the system?
          </p>
          <div className="buttons">
            <GuiButton
              className="btn-outline"
              onClick={(e) => setShowDeleteContactModal(false)}
            >
              Cancel
            </GuiButton>
            <GuiButton
              className="warning"
              onClick={(e) => {
                e.stopPropagation();
                deleteCustomContact(editPopupData?.id);
              }}
            >
              Delete {editPopupData?.label}
            </GuiButton>
          </div>
        </Modal>
      )}
    </>
  );
};

export default AddContactPopup;
